import { useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileSearch, ScrollText, Layers, LogOut, ChevronLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/equilibrium/console", end: true, label: "Overview", icon: LayoutDashboard, testid: "eq-console-nav-overview" },
  { to: "/equilibrium/console/diagnostics", label: "Diagnostics", icon: FileSearch, testid: "eq-console-nav-diagnostics" },
  { to: "/equilibrium/console/provenance", label: "Provenance", icon: ScrollText, testid: "eq-console-nav-provenance" },
  { to: "/equilibrium/console/modules", label: "Modules", icon: Layers, testid: "eq-console-nav-modules" },
];

export default function ConsoleLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Equilibrium-AI — Operational Intelligence";
    return () => {
      document.title = "Axiom-AI — Operator-Grade Supply Chain Intelligence";
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/equilibrium/login");
  };

  return (
    <div className="min-h-screen bg-axiom-bg flex" data-testid="eq-console-layout">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-white/5 bg-axiom-surface/40 hidden md:flex flex-col">
        <div className="px-5 py-5 border-b border-white/5">
          <Link to="/equilibrium" className="flex items-center gap-2 text-xs font-mono text-axiom-muted hover:text-axiom-text">
            <ChevronLeft size={12} /> Platform
          </Link>
          <div className="mt-2 flex items-center gap-2">
            <span className="signal-dot animate-pulse-signal" style={{ width: 6, height: 6 }} />
            <span className="font-display text-lg font-bold tracking-tight text-axiom-text">
              CONSOLE
            </span>
          </div>
          <div className="num-label mt-1">v0.42 / OPERATOR</div>
        </div>
        <nav className="flex-1 py-3">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-2 ${
                  isActive
                    ? "border-axiom-accent bg-axiom-accent/5 text-axiom-text"
                    : "border-transparent text-axiom-muted hover:text-axiom-text hover:bg-white/[0.02]"
                }`
              }
            >
              <n.icon size={14} strokeWidth={1.6} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/5 p-4">
          <div className="num-label">SIGNED IN</div>
          <div className="text-xs font-mono text-axiom-text break-words mt-1">
            {user?.email}
          </div>
          <button
            data-testid="eq-console-logout"
            onClick={handleLogout}
            className="mt-3 btn-axiom-outline text-xs w-full justify-center"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        {/* Mobile sticky bar */}
        <div className="md:hidden border-b border-white/5 bg-axiom-bg/85 backdrop-blur-xl">
          <div className="px-4 h-14 flex items-center justify-between">
            <Link to="/equilibrium" className="flex items-center gap-2">
              <span className="signal-dot animate-pulse-signal" style={{ width: 6, height: 6 }} />
              <span className="font-display text-sm font-bold tracking-tight">CONSOLE</span>
            </Link>
            <button onClick={handleLogout} className="text-xs text-axiom-muted">
              <LogOut size={12} className="inline" /> sign out
            </button>
          </div>
          <div className="px-2 pb-2 overflow-x-auto">
            <div className="flex gap-1 w-max">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) =>
                    `text-xs whitespace-nowrap px-3 py-1.5 border ${
                      isActive
                        ? "border-axiom-accent text-axiom-accent"
                        : "border-white/10 text-axiom-muted"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 lg:p-10">{children}</div>
      </div>
    </div>
  );
}
